import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FileText, Paperclip, Plus, Upload, Trash2, Pencil, ChevronDown, ChevronRight,
  Loader2, Download, ArrowUp, ArrowDown, AlertTriangle, Lock,
} from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import TaskDescriptionMarkdown from '../Tarefas/TaskDescriptionMarkdown';
import DocumentoEditorModal from './DocumentoEditorModal';
import {
  caminhoNoBucket, formatarTamanho, ordenarDocumentos, proximaOrdem,
  resumirDocumentacao, tipoSugeridoPara, tituloSugeridoPara, validarArquivo,
  ehAnexoPrivado, podeBaixarAnexo, BUCKET_ANEXOS, EXTENSOES_ACEITAS,
} from '../../src/lib/projetos/documentos';
import type { ProjetoDocumento, Profile } from '../../types';

interface Props {
  projetoId: number;
  /** Para o aviso de "concluído sem documentação" — a régua está em documentos.ts. */
  etapa: string;
  users: Profile[];
  /** Avisa a tela-pai para o contador da aba e o alerta do cabeçalho acompanharem. */
  onMudou?: (docs: ProjetoDocumento[]) => void;
}

const SELECT = '*, autor:profiles!projeto_documentos_criado_por_fkey(id, full_name, avatar_url)';

/**
 * Linha de anexo. Separada do painel porque tem três estados visuais distintos (liberado,
 * baixando, bloqueado) e embutir isso no map deixava o JSX ilegível.
 *
 * Sem permissão a linha continua VISÍVEL, só que sem download: esconder faria a pessoa
 * achar que não existe anexo e pedir por outro canal. Ver o nome e saber a quem pedir é
 * melhor do que não saber que o arquivo existe.
 */
const AnexoLinha: React.FC<{
  doc: ProjetoDocumento;
  podeBaixar: boolean;
  baixando: boolean;
  autor: string;
  onBaixar: () => void;
}> = ({ doc, podeBaixar, baixando, autor, onBaixar }) => {
  const privado = ehAnexoPrivado(doc.arquivo_url);
  const bloqueado = privado && !podeBaixar;
  const legenda = `${doc.arquivo_nome || ''}${doc.arquivo_tamanho ? ` · ${formatarTamanho(doc.arquivo_tamanho)}` : ''} · ${autor}`;

  const miolo = (
    <>
      {baixando
        ? <Loader2 size={15} className="text-gray-400 shrink-0 ml-[23px] animate-spin" />
        : bloqueado
          ? <Lock size={15} className="text-gray-400 shrink-0 ml-[23px]" />
          : <Paperclip size={15} className="text-gray-400 shrink-0 ml-[23px]" />}
      <span className="min-w-0">
        <span className={`block text-sm font-semibold truncate ${bloqueado ? 'text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-white group-hover:text-primary'}`}>
          {doc.titulo}
        </span>
        <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
          {bloqueado ? 'Contém dado de cliente — acesso restrito a analista, gerente e admin' : legenda}
        </span>
      </span>
    </>
  );

  if (bloqueado) {
    return (
      <div className="flex items-center gap-2 min-w-0 flex-1" title="Você não tem permissão para baixar este anexo">
        {miolo}
      </div>
    );
  }

  // Anexo privado abre por URL assinada gerada no clique, então é botão, não link.
  // O legado do bucket público continua sendo <a href>, que preserva abrir em nova aba.
  if (privado) {
    return (
      <button type="button" onClick={onBaixar} disabled={baixando}
        className="flex items-center gap-2 min-w-0 flex-1 text-left group disabled:opacity-60">
        {miolo}
        <Download size={14} className="text-gray-400 group-hover:text-primary shrink-0" />
      </button>
    );
  }

  return (
    <a href={doc.arquivo_url || '#'} target="_blank" rel="noopener noreferrer"
      className="flex items-center gap-2 min-w-0 flex-1 group">
      {miolo}
      <Download size={14} className="text-gray-400 group-hover:text-primary shrink-0" />
    </a>
  );
};

/**
 * Traduz o erro cru do PostgREST para algo que quem não é dev entende.
 * 42P01 = tabela ainda não existe (a migration `projeto_documentos` não foi aplicada em
 * produção — situação real deste módulo até a migration rodar); 42501 = RLS barrou a
 * operação. Mesmo padrão de `EsteiraCobrancaView.tsx`.
 */
const mensagemAmigavel = (e: any): string => {
  if (e?.code === '42P01') {
    return 'A documentação ainda não está disponível neste ambiente — a tabela do banco ainda não foi criada (migration projeto_documentos pendente).';
  }
  if (e?.code === '42501') {
    return 'Você não tem permissão para fazer isso.';
  }
  return e?.message || 'Algo deu errado. Tente novamente.';
};

/**
 * Aba "Documentação": o que sobra do projeto para quem entra depois.
 *
 * O módulo Projetos nasceu justamente porque o Inbox Omnichannel era acompanhado em .md
 * soltos na raiz do repositório (ver comentário da migration 20260908100000). Esta aba
 * fecha esse buraco: o texto passa a morar no VigorHub, legível por quem não é dev.
 *
 * Documento aparece expansível e renderizado na própria lista — abrir outra tela para ler
 * é atrito suficiente para ninguém ler. Anexo é linha com download.
 */
const ProjetoDocumentacaoPanel: React.FC<Props> = ({ projetoId, etapa, users, onMudou }) => {
  const { profile } = useAuth();
  const [docs, setDocs] = useState<ProjetoDocumento[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [abertos, setAbertos] = useState<Set<number>>(new Set());
  const [editorAberto, setEditorAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState<ProjetoDocumento | null>(null);
  const [baixando, setBaixando] = useState<number | null>(null);
  const inputArquivo = useRef<HTMLInputElement>(null);
  const podeBaixarAnexoAqui = podeBaixarAnexo(profile?.role);

  const aplicar = (lista: ProjetoDocumento[]) => {
    const ordenada = ordenarDocumentos(lista);
    setDocs(ordenada);
    onMudou?.(ordenada);
  };

  const carregar = async () => {
    const { data, error } = await supabase.from('projeto_documentos')
      .select(SELECT).eq('projeto_id', projetoId);
    if (error) setErro(mensagemAmigavel(error));
    else aplicar((data || []) as ProjetoDocumento[]);
    setCarregando(false);
  };

  useEffect(() => { carregar(); /* eslint-disable-next-line */ }, [projetoId]);

  const resumo = useMemo(() => resumirDocumentacao(docs), [docs]);
  const nomeDoAutor = (doc: ProjetoDocumento) =>
    doc.autor?.full_name || users.find(u => u.id === doc.criado_por)?.full_name || 'Alguém do time';

  const alternar = (id: number) => setAbertos(prev => {
    const proximo = new Set(prev);
    proximo.has(id) ? proximo.delete(id) : proximo.add(id);
    return proximo;
  });

  // `emEdicao?.id` e não `emEdicao`: o import de .md abre o editor com um rascunho de id 0
  // (tem conteúdo para pré-preencher, mas ainda não existe no banco) — testar só a
  // presença do objeto mandaria um UPDATE em `id = 0`.
  const salvarDocumento = async ({ titulo, conteudo }: { titulo: string; conteudo: string }) => {
    if (emEdicao?.id) {
      const alvo = emEdicao.id;
      const { data, error } = await supabase.from('projeto_documentos')
        .update({ titulo, conteudo, atualizado_por: profile?.id, updated_at: new Date().toISOString() })
        .eq('id', alvo).select(SELECT).single();
      if (error) throw new Error(mensagemAmigavel(error));
      aplicar(docs.map(d => (d.id === alvo ? (data as ProjetoDocumento) : d)));
      return;
    }
    const { data, error } = await supabase.from('projeto_documentos').insert({
      projeto_id: projetoId, tipo: 'documento', titulo, conteudo,
      ordem: proximaOrdem(docs), criado_por: profile?.id, atualizado_por: profile?.id,
    }).select(SELECT).single();
    if (error) throw new Error(mensagemAmigavel(error));
    aplicar([...docs, data as ProjetoDocumento]);
    setAbertos(prev => new Set(prev).add((data as ProjetoDocumento).id)); // novo já entra aberto
  };

  const anexar = async (arquivo: File) => {
    setErro(null);
    const validacao = validarArquivo(arquivo.name, arquivo.size);
    if (!validacao.ok) { setErro(validacao.erro!); return; }

    // .md e .txt não viram anexo: o texto tem que ficar legível na tela, então o arquivo
    // abre o editor já preenchido em vez de virar um download.
    if (tipoSugeridoPara(arquivo.name) === 'documento') {
      const conteudo = await arquivo.text();
      setEmEdicao({
        id: 0, projeto_id: projetoId, tipo: 'documento',
        titulo: tituloSugeridoPara(arquivo.name), conteudo, ordem: 0,
        created_at: '', updated_at: '',
      } as ProjetoDocumento);
      // id 0 = ainda não existe: o editor trata como novo na hora de salvar.
      setEditorAberto(true);
      return;
    }

    setEnviando(true);
    try {
      const caminho = caminhoNoBucket(projetoId, arquivo.name);
      const { error: erroUpload } = await supabase.storage.from('tarefas-anexos').upload(caminho, arquivo);
      if (erroUpload) throw new Error(erroUpload.message || 'Não foi possível enviar o arquivo.');
      const { data: { publicUrl } } = supabase.storage.from('tarefas-anexos').getPublicUrl(caminho);

      const { data, error } = await supabase.from('projeto_documentos').insert({
        projeto_id: projetoId, tipo: 'anexo', titulo: tituloSugeridoPara(arquivo.name),
        arquivo_url: publicUrl, arquivo_nome: arquivo.name, arquivo_tamanho: arquivo.size,
        ordem: proximaOrdem(docs), criado_por: profile?.id, atualizado_por: profile?.id,
      }).select(SELECT).single();
      if (error) throw new Error(mensagemAmigavel(error));
      aplicar([...docs, data as ProjetoDocumento]);
    } catch (e: any) {
      setErro(e?.message || 'Não foi possível anexar o arquivo.');
    } finally {
      setEnviando(false);
    }
  };

  /**
   * Abre o anexo privado por URL assinada de 60s, gerada no clique. Curta de proposito: o
   * link nao deve sobreviver a um encaminhamento de mensagem.
   */
  const baixarAnexo = async (doc: ProjetoDocumento) => {
    if (!doc.arquivo_url) return;
    setErro(null);
    setBaixando(doc.id);
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET_ANEXOS).createSignedUrl(doc.arquivo_url, 60);
      if (error || !data?.signedUrl) throw error || new Error('Link nao gerado.');
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      setErro(mensagemAmigavel(e));
    } finally {
      setBaixando(null);
    }
  };

  const remover = async (doc: ProjetoDocumento) => {
    if (!window.confirm(`Remover "${doc.titulo}"? Isso não pode ser desfeito.`)) return;
    const { error } = await supabase.from('projeto_documentos').delete().eq('id', doc.id);
    if (error) { setErro(mensagemAmigavel(error)); return; }
    aplicar(docs.filter(d => d.id !== doc.id));
  };

  /**
   * Reordena trocando a `ordem` com o vizinho. Grava as duas linhas; se a segunda falhar,
   * recarrega do banco — melhor a lista voltar ao que o banco tem do que ficar com duas
   * posições iguais na tela.
   */
  const mover = async (indice: number, direcao: -1 | 1) => {
    const vizinho = indice + direcao;
    if (vizinho < 0 || vizinho >= docs.length) return;
    const a = docs[indice], b = docs[vizinho];
    const nova = [...docs];
    nova[indice] = { ...a, ordem: b.ordem };
    nova[vizinho] = { ...b, ordem: a.ordem };
    aplicar(nova);
    const r1 = await supabase.from('projeto_documentos').update({ ordem: b.ordem }).eq('id', a.id);
    const r2 = await supabase.from('projeto_documentos').update({ ordem: a.ordem }).eq('id', b.id);
    if (r1.error || r2.error) { setErro('A nova ordem não foi salva.'); carregar(); }
  };

  if (carregando) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 p-4">
        <Loader2 size={14} className="animate-spin" /> Carregando documentação...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {etapa === 'concluido' && !resumo.documentado && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-4 py-3">
          <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <span className="font-semibold">Projeto concluído sem documentação.</span>{' '}
            Escreva ao menos um documento explicando o que foi feito e o que quem assumir
            precisa saber — é o que evita ter que reconstruir o contexto na próxima vez.
          </p>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex-wrap sm:flex-nowrap">
          <div>
            <h2 className="text-sm font-bold text-gray-800 dark:text-white">Documentação</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {resumo.total === 0
                ? 'O que quem entrar no projeto depois precisa ler.'
                : `${resumo.documentos} documento${resumo.documentos === 1 ? '' : 's'} · ${resumo.anexos} anexo${resumo.anexos === 1 ? '' : 's'}`}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => inputArquivo.current?.click()} disabled={enviando}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary hover:text-primary disabled:opacity-50">
              {enviando ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {enviando ? 'Enviando...' : 'Anexar'}
            </button>
            <button onClick={() => { setEmEdicao(null); setEditorAberto(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white hover:opacity-90">
              <Plus size={13} /> Novo documento
            </button>
          </div>
          <input ref={inputArquivo} type="file" className="hidden" accept={EXTENSOES_ACEITAS.join(',')}
            onChange={e => { const f = e.target.files?.[0]; if (f) anexar(f); e.target.value = ''; }} />
        </div>

        {erro && (
          <p className="px-5 py-2 text-xs text-rose-600 dark:text-rose-400 border-b border-gray-100 dark:border-gray-700" role="alert">
            {erro}
          </p>
        )}

        {docs.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <FileText size={28} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Nada documentado ainda.</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-md mx-auto">
              Escreva o que foi decidido, o que ficou de fora e onde estão as pontas soltas.
              Dá para colar um arquivo .md pronto — ele fica legível aqui dentro.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {docs.map((doc, i) => {
              const aberto = abertos.has(doc.id);
              return (
                <li key={doc.id} className="px-5 py-3">
                  <div className="flex items-start gap-3">
                    {doc.tipo === 'documento' ? (
                      <button onClick={() => alternar(doc.id)} aria-expanded={aberto}
                        aria-label={aberto ? `Recolher ${doc.titulo}` : `Ler ${doc.titulo}`}
                        className="flex items-center gap-2 min-w-0 flex-1 text-left group">
                        {aberto ? <ChevronDown size={15} className="text-gray-400 shrink-0" />
                          : <ChevronRight size={15} className="text-gray-400 shrink-0" />}
                        <FileText size={15} className="text-primary shrink-0" />
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-gray-800 dark:text-white truncate group-hover:text-primary">
                            {doc.titulo}
                          </span>
                          <span className="block text-xs text-gray-500 dark:text-gray-400">
                            {nomeDoAutor(doc)} · {new Date(doc.updated_at || doc.created_at).toLocaleDateString('pt-BR')}
                          </span>
                        </span>
                      </button>
                    ) : (
                      <AnexoLinha
                        doc={doc}
                        podeBaixar={podeBaixarAnexoAqui}
                        baixando={baixando === doc.id}
                        onBaixar={() => baixarAnexo(doc)}
                        autor={nomeDoAutor(doc)}
                      />
                    )}

                    <div className="flex items-center gap-0.5 shrink-0">
                      <button onClick={() => mover(i, -1)} disabled={i === 0} aria-label="Mover para cima"
                        className="p-1.5 text-gray-400 hover:text-primary disabled:opacity-30 disabled:hover:text-gray-400">
                        <ArrowUp size={13} />
                      </button>
                      <button onClick={() => mover(i, 1)} disabled={i === docs.length - 1} aria-label="Mover para baixo"
                        className="p-1.5 text-gray-400 hover:text-primary disabled:opacity-30 disabled:hover:text-gray-400">
                        <ArrowDown size={13} />
                      </button>
                      {doc.tipo === 'documento' && (
                        <button onClick={() => { setEmEdicao(doc); setEditorAberto(true); }} aria-label={`Editar ${doc.titulo}`}
                          className="p-1.5 text-gray-400 hover:text-primary">
                          <Pencil size={13} />
                        </button>
                      )}
                      <button onClick={() => remover(doc)} aria-label={`Remover ${doc.titulo}`}
                        className="p-1.5 text-gray-400 hover:text-rose-600">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>

                  {doc.tipo === 'documento' && aberto && (
                    <div className="mt-3 ml-[46px] pl-4 border-l-2 border-gray-100 dark:border-gray-700">
                      <TaskDescriptionMarkdown value={doc.conteudo || ''} variant="full" />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {editorAberto && (
        <DocumentoEditorModal
          documento={emEdicao}
          onFechar={() => { setEditorAberto(false); setEmEdicao(null); }}
          onSalvar={salvarDocumento}
        />
      )}
    </div>
  );
};

export default ProjetoDocumentacaoPanel;
